import type { LscArchiveItem, LscLiveDashboardRow, LscMatch } from '../types';
import { parseStageSortKey } from '../leagueScheduleSort';
import { resolveTeamVisual } from '../cfbBranding/teamVisualResolver';
import type { GroupedLiveGame } from './groupLiveRowsByMatch';

export type GamesTab = 'live' | 'upcoming' | 'replays' | 'results';

export type FinalScore = { away: number; home: number };

export type WeekGroup<T> = { week: string; items: T[] };

export type ReplayRow = {
  key: string;
  week: string;
  away: string;
  home: string;
  score: FinalScore | null;
  archive: LscArchiveItem | null;
  match: LscMatch | null;
};

export function parseGamesTab(value: string | null, liveCount: number): GamesTab {
  if (value === 'live' || value === 'upcoming' || value === 'replays' || value === 'results') {
    return value;
  }
  return liveCount > 0 ? 'live' : 'replays';
}

/** Replays are games a member can watch. A score without a published replay is a result. */
export function rowsWithPublishedReplay(rows: ReplayRow[]): ReplayRow[] {
  return rows.filter((row) => Boolean(row.archive));
}

/** A real pair of numbers, including a genuine 0. Missing scores stay blank. */
export function finalScore(
  source: { away_score?: number | null; home_score?: number | null } | null | undefined
): FinalScore | null {
  if (!source) return null;
  const away = source.away_score;
  const home = source.home_score;
  if (typeof away !== 'number' || typeof home !== 'number') return null;
  if (!Number.isFinite(away) || !Number.isFinite(home)) return null;
  return { away, home };
}

export function winningSide(score: FinalScore): 'away' | 'home' | null {
  if (score.away > score.home) return 'away';
  if (score.home > score.away) return 'home';
  return null;
}

export function teamAbbreviation(label: string | null | undefined, gameKey?: string | null): string {
  return resolveTeamVisual({
    label: label?.trim() || 'Team',
    gameKey: gameKey || undefined,
  }).abbreviation;
}

function sameTeam(label: string | null | undefined, abbreviation: string, gameKey?: string | null): boolean {
  const wanted = abbreviation.trim().toLowerCase();
  if (!wanted) return true;
  return teamAbbreviation(label, gameKey).toLowerCase() === wanted;
}

export function groupByWeek<T>(
  items: T[],
  weekOf: (item: T) => string,
  direction: 'asc' | 'desc'
): WeekGroup<T>[] {
  const buckets = new Map<string, T[]>();
  for (const item of items) {
    const week = weekOf(item).trim() || 'Week';
    const bucket = buckets.get(week);
    if (bucket) bucket.push(item);
    else buckets.set(week, [item]);
  }
  const weeks = Array.from(buckets.keys()).sort((left, right) => {
    const delta = parseStageSortKey(left) - parseStageSortKey(right);
    if (delta !== 0) return direction === 'desc' ? -delta : delta;
    return direction === 'desc' ? right.localeCompare(left) : left.localeCompare(right);
  });
  return weeks.map((week) => ({ week, items: buckets.get(week) ?? [] }));
}

function inSeason(season: string | null | undefined, activeSeason: string): boolean {
  if (!activeSeason) return true;
  return (season ?? '').trim() === activeSeason;
}

function playedStatus(match: LscMatch): boolean {
  const status = (match.status ?? '').trim().toLowerCase();
  return status === 'final' || status === 'completed' || status === 'complete';
}

function liveMatchIds(games: GroupedLiveGame[]): Set<string> {
  return new Set(
    games
      .map((game) => (game.competitionMatchId ?? '').trim())
      .filter((id) => id.length > 0)
  );
}

function teamKey(away?: string | null, home?: string | null): string {
  return `${(away ?? '').trim().toLowerCase()}@${(home ?? '').trim().toLowerCase()}`;
}

/** A verified live row matches the schedule by id, external id, or the same matchup. */
export function matchIsOnLiveSlate(match: LscMatch, live: GroupedLiveGame[]): boolean {
  const ids = liveMatchIds(live);
  if (ids.has(match.id)) return true;
  const external = (match.external_match_id ?? '').trim();
  if (external && ids.has(external)) return true;
  const key = teamKey(match.away_side_label, match.home_side_label);
  if (key === '@') return false;
  return live.some((game) =>
    game.sources.some((source) => teamKey(source.row.away_team, source.row.home_team) === key)
  );
}

/**
 * Upcoming starts at the current stage pointer.
 * A live game in a later week pulls the floor forward when the pointer is behind.
 * Null means there is no pointer and no live week, so nothing is hidden.
 */
export function upcomingStageFloor(
  activeStage: string | null | undefined,
  live: GroupedLiveGame[]
): number | null {
  const keys: number[] = [];
  const pointer = parseStageSortKey(activeStage);
  if ((activeStage ?? '').trim() && pointer < 5000) keys.push(pointer);
  for (const game of live) {
    const key = parseStageSortKey(game.lead.row.stage_label);
    if ((game.lead.row.stage_label ?? '').trim() && key < 5000) keys.push(key);
  }
  if (!keys.length) return null;
  return Math.max(...keys);
}

export function isBeforeUpcomingFloor(stageLabel: string | null | undefined, floor: number | null): boolean {
  if (floor == null) return false;
  const key = parseStageSortKey(stageLabel);
  if (key >= 5000) return false;
  return key < floor;
}

export function unplayedStatusLabel(stageLabel: string | null | undefined, floor: number | null): string {
  return isBeforeUpcomingFloor(stageLabel, floor) ? 'Not played' : 'Not played yet';
}

function archivesByMatch(archives: LscArchiveItem[]): Map<string, LscArchiveItem> {
  const map = new Map<string, LscArchiveItem>();
  for (const archive of archives) {
    const id = (archive.competition_match_id ?? '').trim();
    if (id && !map.has(id)) map.set(id, archive);
  }
  return map;
}

function matchForArchive(archive: LscArchiveItem, matches: LscMatch[]): LscMatch | null {
  const id = (archive.competition_match_id ?? '').trim();
  if (!id) return null;
  return (
    matches.find((match) => match.id === id) ??
    matches.find((match) => (match.external_match_id ?? '').trim() === id) ??
    null
  );
}

export function seasonScopedMatches(matches: LscMatch[], activeSeason: string): LscMatch[] {
  return matches.filter((match) => inSeason(match.season_label, activeSeason));
}

function rowMatchesTeam(
  away: string | null | undefined,
  home: string | null | undefined,
  team: string,
  gameKey?: string | null
): boolean {
  if (!team.trim()) return true;
  return sameTeam(away, team, gameKey) || sameTeam(home, team, gameKey);
}

function loginMatches(login: string | null | undefined, broadcaster: string): boolean {
  if (!broadcaster.trim()) return true;
  return (login ?? '').trim().toLowerCase() === broadcaster.trim().toLowerCase();
}

export function filterLiveGames(
  games: GroupedLiveGame[],
  filters: { week: string; team: string; broadcaster: string },
  gameKey?: string | null
): GroupedLiveGame[] {
  return games.filter((game) => {
    const row = game.lead.row;
    if (filters.week && (row.stage_label ?? '').trim() !== filters.week) return false;
    if (!rowMatchesTeam(row.away_team, row.home_team, filters.team, gameKey)) return false;
    if (
      filters.broadcaster &&
      !game.sources.some((source) => loginMatches(source.row.twitch_login, filters.broadcaster))
    ) {
      return false;
    }
    return true;
  });
}

export function buildReplayRows(
  matches: LscMatch[],
  archives: LscArchiveItem[],
  live: GroupedLiveGame[],
  activeSeason: string
): ReplayRow[] {
  const liveIds = liveMatchIds(live);
  const byMatch = archivesByMatch(archives);
  const usedArchives = new Set<string>();
  const rows: ReplayRow[] = [];

  for (const match of seasonScopedMatches(matches, activeSeason)) {
    if (liveIds.has(match.id)) continue;
    const archive = byMatch.get(match.id) ?? null;
    const score = finalScore(match);
    if (!archive && !score && !playedStatus(match)) continue;
    if (archive) usedArchives.add(archive.id);
    rows.push({
      key: match.id,
      week: (match.stage_label ?? archive?.match_summary?.stage_label ?? '').trim(),
      away: match.away_side_label?.trim() || 'Away',
      home: match.home_side_label?.trim() || 'Home',
      score,
      archive,
      match,
    });
  }

  for (const archive of archives) {
    if (usedArchives.has(archive.id)) continue;
    const match = matchForArchive(archive, matches);
    if (match && liveIds.has(match.id)) continue;
    const season = match?.season_label ?? archive.match_summary?.season_label;
    if (!inSeason(season, activeSeason)) continue;
    rows.push({
      key: archive.id,
      week: (archive.match_summary?.stage_label ?? match?.stage_label ?? '').trim(),
      away: archive.match_summary?.away_side_label?.trim() || match?.away_side_label?.trim() || 'Away',
      home: archive.match_summary?.home_side_label?.trim() || match?.home_side_label?.trim() || 'Home',
      score: finalScore(match),
      archive,
      match,
    });
  }

  return rows;
}

export function filterReplayRows(
  rows: ReplayRow[],
  filters: { week: string; team: string; broadcaster: string },
  gameKey?: string | null
): ReplayRow[] {
  return rows.filter((row) => {
    if (filters.week && row.week !== filters.week) return false;
    if (!rowMatchesTeam(row.away, row.home, filters.team, gameKey)) return false;
    if (filters.broadcaster) {
      const sources = row.archive?.sources?.length ? row.archive.sources : row.archive ? [row.archive] : [];
      if (!sources.some((source) => loginMatches(source.provider_broadcaster_login, filters.broadcaster))) {
        return false;
      }
    }
    return true;
  });
}

export function buildUpcoming(
  matches: LscMatch[],
  archives: LscArchiveItem[],
  live: GroupedLiveGame[],
  activeSeason: string
): LscMatch[] {
  const archived = new Set(
    archives
      .map((archive) => (archive.competition_match_id ?? '').trim())
      .filter((id) => id.length > 0)
  );
  return seasonScopedMatches(matches, activeSeason).filter((match) => {
    const external = (match.external_match_id ?? '').trim();
    if (matchIsOnLiveSlate(match, live) || archived.has(match.id) || (external && archived.has(external))) {
      return false;
    }
    if (finalScore(match) || playedStatus(match)) return false;
    const lifecycle = (match.lifecycle_status ?? 'active').trim().toLowerCase();
    return lifecycle === 'active' || lifecycle === '';
  });
}

/** Default Upcoming hides weeks before the floor. `week=all` and a chosen week do not. */
export function applyUpcomingWindow(
  matches: LscMatch[],
  floor: number | null,
  selectedWeek: string
): LscMatch[] {
  if (selectedWeek.trim()) return matches;
  return matches.filter((match) => !isBeforeUpcomingFloor(match.stage_label, floor));
}

export function filterUpcoming(
  matches: LscMatch[],
  filters: { week: string; team: string; broadcaster: string },
  gameKey?: string | null
): LscMatch[] {
  if (filters.broadcaster.trim()) return [];
  return matches.filter((match) => {
    if (filters.week && (match.stage_label ?? '').trim() !== filters.week) return false;
    return rowMatchesTeam(match.away_side_label, match.home_side_label, filters.team, gameKey);
  });
}

export function weekOptions(
  matches: LscMatch[],
  archives: LscArchiveItem[],
  live: GroupedLiveGame[]
): string[] {
  const labels = new Set<string>();
  for (const match of matches) {
    const label = (match.stage_label ?? '').trim();
    if (label) labels.add(label);
  }
  for (const archive of archives) {
    const label = (archive.match_summary?.stage_label ?? '').trim();
    if (label) labels.add(label);
  }
  for (const game of live) {
    const label = (game.lead.row.stage_label ?? '').trim();
    if (label) labels.add(label);
  }
  return Array.from(labels).sort((left, right) => parseStageSortKey(left) - parseStageSortKey(right));
}

export function teamOptions(matches: LscMatch[], gameKey?: string | null): string[] {
  const labels = new Set<string>();
  for (const match of matches) {
    for (const side of [match.away_side_label, match.home_side_label]) {
      const abbreviation = teamAbbreviation(side, gameKey);
      if (abbreviation && abbreviation !== 'Team') labels.add(abbreviation);
    }
  }
  return Array.from(labels).sort((left, right) => left.localeCompare(right));
}

export function broadcasterOptions(
  archives: LscArchiveItem[],
  liveRows: LscLiveDashboardRow[]
): string[] {
  const logins = new Set<string>();
  for (const archive of archives) {
    const sources = archive.sources?.length ? archive.sources : [archive];
    for (const source of sources) {
      const login = source.provider_broadcaster_login?.trim();
      if (login) logins.add(login);
    }
  }
  for (const row of liveRows) {
    const login = row.twitch_login?.trim();
    if (login) logins.add(login);
  }
  return Array.from(logins).sort((left, right) => left.localeCompare(right));
}
